import { toast } from "@/components/common/Toast"
import {
  getApiErrorCode,
  getApiErrorStatus,
  toastApiError,
} from "@/features/material-compliance/api/apiError"
import { logoErrorKey } from "@/features/material-compliance/api/templateSettingsApi"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  useDeleteOrgLogo,
  useUploadOrgLogo,
} from "@/services/api/organizationApi"

/**
 * Upload and remove the organization logo, with their toasts. Shared by the
 * General settings page and the declaration template editor, so both report
 * the same errors the same way. Both write straight through: no Save step.
 *
 * `onChanged`: fired after a successful upload or remove, for a caller whose
 * own data embeds the logo (a template draft's rendered letterhead).
 */
export function useOrgLogoActions(onChanged?: () => void) {
  const { translate } = useTranslation()
  const uploadLogo = useUploadOrgLogo()
  const deleteLogo = useDeleteOrgLogo()

  async function upload(file: File) {
    try {
      await uploadLogo.mutateAsync(file)
      toast.success(translate("MC_ORG_SETTINGS_TOAST_LOGO_UPLOADED"))
      onChanged?.()
    } catch (err: unknown) {
      // A type or size refusal is named from its error code, never from the
      // backend's English message; anything else gets the generic line, and a
      // 403 never reached the API at all. toastApiError skips 5xx and limit
      // errors, which the apiClient interceptor already toasted.
      toastApiError(
        err,
        translate,
        logoErrorKey(getApiErrorCode(err) ?? undefined) ??
          (getApiErrorStatus(err) === 403
            ? "MC_ORG_SETTINGS_TOAST_LOGO_BLOCKED"
            : "MC_ORG_SETTINGS_TOAST_LOGO_UPLOAD_ERR"),
      )
    }
  }

  /** Resolves to whether the logo was removed. */
  async function remove(): Promise<boolean> {
    if (deleteLogo.isPending) return false
    try {
      await deleteLogo.mutateAsync()
      toast.success(translate("MC_ORG_SETTINGS_TOAST_LOGO_REMOVED"))
      onChanged?.()
      return true
    } catch (err: unknown) {
      toastApiError(err, translate, "MC_ORG_SETTINGS_TOAST_LOGO_REMOVE_ERR")
      return false
    }
  }

  return {
    upload,
    remove,
    isUploading: uploadLogo.isPending,
    isRemoving: deleteLogo.isPending,
  }
}

/** File types the logo endpoint accepts. */
export const ORG_LOGO_ACCEPT = "image/png,image/jpeg,image/svg+xml"
